import { Graphics } from "pixi.js"
import { DyanmicTextContainer } from "../gui/dynamic_text_container"
import { BasicSprite } from "../sprites/basic_sprite"
import { AnimatedFeedbackParams, TileSize, randomBool, randomIntBetween } from "game-common/models"
import { Throbber } from "../gui/throbber"
import { fitItemIcon, PACK_SLOT } from "../gui/fit_item_icon"

export class AnimatedFeedback {
    private itemPickupQueueInterval: any
    private itemPickupQueue: any[] = []
    private isExecuting: boolean = false

    remove = (job: any) => {
        this.itemPickupQueue = this.itemPickupQueue.filter(n => n !== job)
    }

    setup(
        x: number,
        y: number,
        image: string,
        label: string,
        parent: Graphics,
        params: AnimatedFeedbackParams = {},
        spriteParent?: Graphics,
    ) {
        const job = () => {
            this.isExecuting = true

            if (params.type === "vibrate" && spriteParent) {
                // this affects the parent
                let startTs = Date.now()
                let shakeDirLeft = randomBool()
                // let shakeDistance = randomIntBetween(1, 1)
                let shakeDistance = 1
                let currentShakeDistance = shakeDistance
                let duration = 750
                let up: boolean = randomBool()
                let shakeOffsetX = 0
                let shakeOffsetY = 0

                let originalX = spriteParent.x
                let originalY = spriteParent.y

                const interval = setInterval(() => {
                    // The body can be destroyed mid-shake (removed as it dies); a destroyed
                    // DisplayObject throws on every position write.
                    if (spriteParent.destroyed) {
                        clearInterval(interval)
                        return
                    }
                    let elapsed = Date.now() - startTs
                    if (elapsed > duration) {
                        clearInterval(interval)
                        shakeOffsetX = 0
                        shakeOffsetY = 0

                        spriteParent.x = originalX
                        spriteParent.y = originalY

                        return
                    }

                    if ((elapsed / duration) % 0.25 === 0 || currentShakeDistance <= 0) {
                        shakeDirLeft = !shakeDirLeft
                        shakeDistance = shakeDistance - 1
                        currentShakeDistance = shakeDistance
                    }

                    currentShakeDistance -= 1
                    shakeOffsetX += shakeDirLeft ? 1 : -1

                    // this.updatePlayerLocation(entity)
                    // shakeOffsetY += up ? -1 : 1

                    spriteParent.x = originalX + shakeOffsetX
                    spriteParent.y = originalY + shakeOffsetY

                    up = !up
                }, 10)
            }

            if (image || label) {
                const sprite = new DyanmicTextContainer()

                if (label) {
                    sprite.append2(label, {
                        leftMargin: 0,
                        color: params?.color,
                        role: params?.font ? undefined : "effect",
                        font: params?.font,
                    })
                }

                if (image) {
                    // The only images that come through here are items -- one picked up, one just
                    // crafted -- so draw the icon the way the pack does. The overlay this rises in
                    // is in screen pixels, like the pack's panel, so fitting the art to the pack's
                    // slot box puts it on screen at the size the player knows it by.
                    const iconBox = new Graphics()
                    const icon = new BasicSprite({
                        color: 0xffffff,
                        scale: 1,
                        name: image,
                        animated: false,
                        rotating: false,
                    })
                    iconBox.addChild(icon)
                    fitItemIcon(icon, 0, 0, PACK_SLOT)

                    sprite.append2(iconBox, {
                        sameLine: true,
                        leftMargin: 8,
                        // Centre the slot box on the "+N" beside it rather than hanging it off its top.
                        topMargin: label ? (sprite.height - PACK_SLOT) / 2 : 0,
                    })
                }

                let throbber: Throbber
                if (params?.alignment === "center") {
                    sprite.x = parent.width / 2 + (label.length > 1 ? -(label.length * 11) : 0)
                    sprite.y = y - TileSize
                    throbber = new Throbber(sprite, {
                        blinkInterval: 10,
                    })

                    throbber.update()
                } else {
                    // default
                    sprite.x = -sprite.width / 4 + x
                    sprite.y = y
                }

                parent.addChild(sprite)

                const now = Date.now()
                const budget = params?.duration || 750
                const { variant = "default" } = params
                const scrollIncrement = (params?.scrollSpeed || 1.0) * 2
                const finish = () => {
                    clearInterval(interval)
                    sprite.parent?.removeChild(sprite)
                    // sprite.remove()
                    this.isExecuting = false

                    if (this.itemPickupQueue.length < 1) {
                        clearInterval(this.itemPickupQueueInterval)
                        this.itemPickupQueueInterval = undefined
                    }
                    if (throbber) {
                        throbber.stop()
                    }
                }
                const interval = setInterval(() => {
                    // `parent` is the body's overlay. When the body is removed while this is still
                    // rising -- the killing blow's damage number on an entity that just died -- it is
                    // destroyed with its children, and every read of a destroyed DisplayObject's
                    // position throws. Without this the interval would throw every tick, forever.
                    if (sprite.destroyed) {
                        finish()
                        return
                    }
                    if (variant === "default") {
                        sprite.y -= scrollIncrement
                    }

                    const elapsed = Date.now() - now
                    const alpha = elapsed / budget
                    if (variant === "default") {
                        if (alpha > 0.7) {
                            sprite.alpha = 1.0 - alpha + 0.2
                        }
                    }
                    if (elapsed > budget) {
                        finish()
                    }
                }, 20)
            }
        }

        if (params.immediate) {
            job()
            return job
        }

        if (this.itemPickupQueueInterval) {
            clearInterval(this.itemPickupQueueInterval)
        }

        this.itemPickupQueueInterval = setInterval(() => {
            if (this.itemPickupQueue.length < 1) {
                clearInterval(this.itemPickupQueueInterval)
                return
            }

            const next = this.itemPickupQueue.shift()
            if (next) {
                next()
            }
        }, 50)

        if (!this.isExecuting) {
            job()
        } else {
            this.itemPickupQueue.push(job)
        }

        return job
    }
}
